import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react'
import { en } from './locales/en'
import { ru } from './locales/ru'

export type Locale = 'en' | 'ru'

export const locales: Record<Locale, { name: string; nativeName: string }> = {
  en: { name: 'English', nativeName: 'English' },
  ru: { name: 'Russian', nativeName: 'Русский' },
}

const translations: Record<Locale, typeof en> = {
  en,
  ru,
}

export type TranslationKey = keyof typeof en

interface I18nContextType {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: TranslationKey) => string
}

const I18nContext = createContext<I18nContextType | undefined>(undefined)

// Multilanguage disabled - always use English
// To re-enable, restore getInitialLocale() with localStorage and browser detection
function getInitialLocale(): Locale {
  return 'en'
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale] = useState<Locale>(getInitialLocale)

  const setLocale = useCallback((_newLocale: Locale) => {
    // Multilanguage disabled - locale change is no-op
    // To re-enable, uncomment:
    // setLocaleState(newLocale)
    // localStorage.setItem('preferred-locale', newLocale)
    // document.documentElement.lang = newLocale
  }, [])

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const t = useCallback(
    (key: TranslationKey): string => {
      return translations[locale][key] || translations['en'][key] || key
    },
    [locale]
  )

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  )
}

export function useTranslation() {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider')
  }
  return context
}
